import { IRecommendedPayerBasedOnEligibility } from "types/Provider/Provider";

export const INACTIVE_PLAN_MESSAGE =
  "The patient coverage shows inactive, please contact the patient or the payer to obtain most current information";

/** Plan names longer than this are shown as the segment that decided the switch instead. */
export const MAX_PLAN_NAME_IN_MESSAGE = 60;

/**
 * CLE-2845 — why a MEDICAID_DUAL switch fired, in words: the API sends the matched keyword, or
 * `PolicyType <value>` when it was pVerify's PolicyType that named Medicaid. CLE-3004 added three
 * more signals the API names: `pVerify: cost-share protected by state Medicaid`, `PolicyType
 * Qualified Medicare Beneficiary` and `PlanNetworkName SNP-DE` / `PlanName SNP-DE`.
 */
export const describeMedicaidMatch = (matchedText?: string | null): string => {
  if (!matchedText) return "the plan looks like Medicaid";
  if (matchedText.startsWith("pVerify: ")) {
    return matchedText.slice("pVerify: ".length);
  }
  if (matchedText === "PolicyType Qualified Medicare Beneficiary") {
    return "PolicyType says Qualified Medicare Beneficiary";
  }
  if (matchedText.startsWith("PolicyType")) return "PolicyType says Medicaid";
  if (/^Plan(Network)?Name SNP-DE$/.test(matchedText)) {
    return "a dual-eligible special needs plan (SNP-DE)";
  }
  return `plan name contains "${matchedText}"`;
};

/**
 * CLE-3004 — the API's `medicaidDetected`: pVerify said Medicaid on a check that was not switched to
 * a Medicaid payer, so every benefit was forced to $0. `signal` names where it was found.
 */
export interface IMedicaidDetected {
  signal: string;
}

/** CLE-3004 — the notice shown when the API zeroed the benefits because pVerify said Medicaid. */
export const buildMedicaidDetectedMessage = (
  detected?: IMedicaidDetected | null
): string | null =>
  detected?.signal
    ? `Medicaid detected (${detected.signal}). Benefits set to $0.`
    : null;

/**
 * CLE-2845 — the message shown after an eligibility check auto-switched the order's payer, worded
 * for the reason the API gave. `previousPayerName` is the payer staff had selected, so they can
 * switch back if the recommendation is wrong.
 *
 * The API reads a switched check's benefits under the RECOMMENDED payer (its network rule, Medicaid
 * and Medicare handling, copay overrides), so switching back leaves benefits read under the other
 * payer on the form — the message says so rather than letting a revert look free.
 */
export const buildPayerSwitchMessage = (
  recommendedPayer: IRecommendedPayerBasedOnEligibility,
  previousPayerName: string,
  planName?: string | null
): string => {
  const revert = `If incorrect, please revert to "${previousPayerName}" and check the benefits below, which were read under "${recommendedPayer.name}."`;

  switch (recommendedPayer.reason) {
    case "MEDICAID_DUAL":
      return `This plan appears to be a Medicaid / dual plan (${describeMedicaidMatch(
        recommendedPayer.matchedText
      )}). We auto-updated the payer to "${recommendedPayer.name}." ${revert}`;
    case "PLAN_NAME": {
      const fullPlanName = planName?.trim() || "";
      const shownPlan =
        !fullPlanName || fullPlanName.length > MAX_PLAN_NAME_IN_MESSAGE
          ? recommendedPayer.matchedText || fullPlanName
          : fullPlanName;
      const reported = shownPlan
        ? `The payer reported plan "${shownPlan}", which is ${recommendedPayer.name}.`
        : `The plan the payer reported is ${recommendedPayer.name}.`;
      return `${reported} We auto-updated the payer to "${recommendedPayer.name}." ${revert}`;
    }
    case "OUT_OF_AREA_DEFAULT":
      return `This appears to be an out-of-area Blue Cross member. This facility prices those as "${recommendedPayer.name}", so we auto-updated the payer. ${revert}`;
    case "MEDICARE_ADVANTAGE":
    default:
      return `Based on the data below, this appears to be a Medicare Advantage plan. We auto-updated the payer to "${recommendedPayer.name}." ${revert}`;
  }
};

/**
 * CLE-2675 — the payer answered, but every benefit it disclosed is out-of-network. The
 * normalized values are zeros of ignorance, not a real $0 plan, so the form is not
 * auto-filled and the user is pointed at the payer (additionalInfo usually carries the
 * payer's own phone number).
 */
export const buildOutOfNetworkOnlyBenefitsMessage = (
  additionalInfo?: string | null
) =>
  `The payer returned only out-of-network benefit information for this plan, so the benefit fields were not filled in and an estimate should not be created from this response. Please contact the payer to verify the patient's in-network benefits, then enter them manually.${
    additionalInfo ? ` Payer info: ${additionalInfo}` : ""
  }`;

/**
 * The message a STORED eligibility response warrants, or null when it warrants none.
 *
 * This is the single source of truth for the four effects (primary/secondary ×
 * fresh-response/reopened-order) that re-derive the eligibility message from
 * `benefitDetails`. Before it existed, each effect had its own copy of the rules and
 * none of them knew about `hasOnlyOutOfNetworkBenefits` — so the effect watching
 * `benefitDetails` fired right after EligibilityButton stored the response and wiped
 * the guard message the button had just set (CLE-2675).
 */
export const deriveStoredEligibilityMessage = (
  response: any
): string | null => {
  if (!response) {
    return null;
  }
  if (response.planStatus === "Inactive") {
    return INACTIVE_PLAN_MESSAGE;
  }
  if (response.hasOnlyOutOfNetworkBenefits) {
    return buildOutOfNetworkOnlyBenefitsMessage(response.additionalInfo);
  }
  const allFieldsAreNull =
    response.familyCalendarYearDeductible === null &&
    response.individualCalendarYearDeductible === null &&
    response.remainingDeductible === null &&
    response.coinsurancePercent === null;
  if (response.isEligibilityEmpty || allFieldsAreNull) {
    return response.additionalInfo || null;
  }
  // CLE-3004 — re-derived from the stored response, so a reopened order says it again.
  return buildMedicaidDetectedMessage(response.medicaidDetected);
};

/** The API's `networkStatusOverride` (CLE-2683 / CLE-2882 / CLE-2883). */
export interface INetworkStatusOverride {
  status: string;
  benefitsRepointed: boolean;
  /** CLE-2883 — decided by the API: true only where the benefit engine quotes. Absent on older responses. */
  exceptionAllowed?: boolean;
}

/** The API's `outOfNetworkException` (CLE-2883): set when an approved exception moved the check in network. */
export interface IOutOfNetworkException {
  approved: boolean;
}

export const OUT_OF_NETWORK_FIGURES_MESSAGE =
  "This facility is out of network for this plan. The benefits below are the payer's out-of-network figures.";

export const OUT_OF_NETWORK_NO_BENEFITS_MESSAGE =
  "This facility is out of network for this plan. This payer does NOT have out-of-network benefits, call to obtain gap or exception.";

export const OUT_OF_NETWORK_EXCEPTION_APPROVED_MESSAGE =
  "Out-of-network exception approved — in-network benefits shown.";

/**
 * CLE-2683 — the account has declared itself out of network for this payer/plan. `benefitsRepointed`
 * says whether the tabs show any out-of-network figure. Since CLE-2882 nothing in-network is shown or
 * filled in on such a plan, so when the payer sent no out-of-network figures the fields are blank and
 * (CLE-2883) staff are told to call the payer for a gap or exception.
 */
export const buildNetworkStatusMessage = (
  override?: INetworkStatusOverride | null
): string | null => {
  if (!override || override.status !== "OutOfNetwork") return null;
  return override.benefitsRepointed
    ? OUT_OF_NETWORK_FIGURES_MESSAGE
    : OUT_OF_NETWORK_NO_BENEFITS_MESSAGE;
};

/**
 * CLE-2883 / CLE-2960 — the label of the button that re-runs the check in network with an approved exception,
 * or null when none is offered. Offered only where the API says the account may record one
 * (`exceptionAllowed`, true only where the benefit engine quotes); this side never works that out itself.
 *
 *   · blank case (no out-of-network figures)  → "Exception is approved" (CLE-2883)
 *   · out-of-network figures shown            → "Run in-network" (CLE-2960: the payer approved in-network
 *                                                for this case, so staff want the in-network benefits instead)
 */
export const RUN_IN_NETWORK_LABEL = "Run in-network";
export const EXCEPTION_APPROVED_LABEL = "Exception is approved";

export const outOfNetworkExceptionButtonLabel = (
  override?: INetworkStatusOverride | null
): string | null => {
  if (
    !override ||
    override.status !== "OutOfNetwork" ||
    override.exceptionAllowed !== true
  ) {
    return null;
  }
  return override.benefitsRepointed
    ? RUN_IN_NETWORK_LABEL
    : EXCEPTION_APPROVED_LABEL;
};

/** CLE-2883 — the info message shown once an approved exception has moved the check in network. */
export const buildOutOfNetworkExceptionMessage = (
  exception?: IOutOfNetworkException | null
): string | null =>
  exception?.approved ? OUT_OF_NETWORK_EXCEPTION_APPROVED_MESSAGE : null;
